import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ArrowRightLeft, Globe, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// ─── Constants ────────────────────────────────────────────────────────────────
const CURRENCIES = [
  { code: 'XOF', label: 'Franc CFA (XOF)', flag: '🇨🇮', symbol: 'FCFA' },
  { code: 'EUR', label: 'Euro (EUR)', flag: '🇪🇺', symbol: '€' },
  { code: 'USD', label: 'Dollar US (USD)', flag: '🇺🇸', symbol: '$' },
  { code: 'GHS', label: 'Cedi Ghanéen (GHS)', flag: '🇬🇭', symbol: '₵' },
];

const PAYOUT_CHANNELS = {
  bank_transfer: { label: 'Virement bancaire', icon: '🏦', desc: 'IBAN / SWIFT — 1-3 jours ouvrés' },
  mobile_money: { label: 'Mobile Money', icon: '📱', desc: 'MTN / Orange / Wave — Instantané' },
  paystack: { label: 'Paystack', icon: '💳', desc: 'Compte Paystack lié — 24h' },
};

// Simulated live FX rates (base: XOF)
const FALLBACK_RATES = { XOF: 1, EUR: 0.001524, USD: 0.001667, GHS: 0.02083 };

const formatAmount = (amount, currency = 'XOF') => {
  if (!amount && amount !== 0) return '—';
  const opts = currency === 'XOF'
    ? { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }
    : { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat('fr-FR', opts)?.format(amount);
};

const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ─── FX Rate Banner ───────────────────────────────────────────────────────────
const FXRateBanner = ({ rates, lastUpdated, onRefresh, loading }) => (
  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 mb-6">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-amber-800">Taux de change en direct — Base XOF</span>
        {lastUpdated && (
          <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
            Mis à jour: {new Date(lastUpdated)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <button
        onClick={onRefresh}
        disabled={loading}
        className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        Actualiser
      </button>
    </div>
    <div className="grid grid-cols-3 gap-3">
      {[{ code: 'EUR', label: 'Euro', flag: '🇪🇺' }, { code: 'USD', label: 'USD', flag: '🇺🇸' }, { code: 'GHS', label: 'Cedi', flag: '🇬🇭' }]?.map(c => (
        <div key={c?.code} className="bg-white rounded-lg p-3 border border-amber-100 text-center">
          <div className="text-lg mb-1">{c?.flag}</div>
          <div className="text-xs text-gray-500 mb-1">{c?.label}</div>
          <div className="text-sm font-bold text-gray-800">
            1 000 XOF = {(1000 * (rates?.[c?.code] || FALLBACK_RATES?.[c?.code]))?.toFixed(c?.code === 'GHS' ? 2 : 3)} {c?.code}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            1 {c?.code} = {Math.round(1 / (rates?.[c?.code] || FALLBACK_RATES?.[c?.code]))?.toLocaleString('fr-FR')} XOF
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Currency Conversion Modal ────────────────────────────────────────────────
const ConversionModal = ({ payout, rates, onClose, onConfirm }) => {
  const [targetCurrency, setTargetCurrency] = useState('EUR');
  const [channel, setChannel] = useState('bank_transfer');
  const [accountDetails, setAccountDetails] = useState('');
  const [note, setNote] = useState('');

  const baseAmount = payout?.commission_amount || 0;
  const rate = rates?.[targetCurrency] || FALLBACK_RATES?.[targetCurrency];
  const convertedAmount = baseAmount * rate;
  const feeRate = channel === 'mobile_money' ? 0.015 : channel === 'paystack' ? 0.015 : 0.005;
  const feeAmount = convertedAmount * feeRate;
  const netAmount = convertedAmount - feeAmount;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Conversion & Paiement</h3>
            <p className="text-sm text-gray-500">{payout?.recipient_name || 'Destinataire'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Source amount */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">Montant source</p>
            <p className="text-2xl font-bold text-gray-900">{formatAmount(baseAmount, 'XOF')}</p>
            <p className="text-xs text-gray-400 mt-1">Commission {payout?.recipient_role === 'agent' ? 'Agent' : 'Notaire'} — {payout?.commission_rate}%</p>
          </div>

          {/* Target currency */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Devise de paiement</label>
            <div className="grid grid-cols-2 gap-2">
              {CURRENCIES?.filter(c => c?.code !== 'XOF')?.map(c => (
                <button
                  key={c?.code}
                  onClick={() => setTargetCurrency(c?.code)}
                  className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${targetCurrency === c?.code ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <span className="text-xl">{c?.flag}</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{c?.code}</p>
                    <p className="text-xs text-gray-500">{c?.symbol}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Conversion breakdown */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <ArrowRightLeft className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-800">Détail de la conversion</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Taux appliqué</span>
              <span className="font-medium">1 XOF = {rate?.toFixed(6)} {targetCurrency}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Montant converti</span>
              <span className="font-medium">{formatAmount(convertedAmount, targetCurrency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Frais de transfert ({(feeRate * 100)?.toFixed(1)}%)</span>
              <span className="text-red-600 font-medium">- {formatAmount(feeAmount, targetCurrency)}</span>
            </div>
            <div className="border-t border-blue-200 pt-2 flex justify-between">
              <span className="font-bold text-gray-800">Montant net reçu</span>
              <span className="font-bold text-green-700 text-lg">{formatAmount(netAmount, targetCurrency)}</span>
            </div>
          </div>

          {/* Payout channel */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Canal de paiement</label>
            <div className="space-y-2">
              {Object.entries(PAYOUT_CHANNELS)?.map(([key, ch]) => (
                <button
                  key={key}
                  onClick={() => setChannel(key)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${channel === key ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <span className="text-xl">{ch?.icon}</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{ch?.label}</p>
                    <p className="text-xs text-gray-500">{ch?.desc}</p>
                  </div>
                  {channel === key && <CheckCircle className="w-4 h-4 text-amber-500 ml-auto" />}
                </button>
              ))}
            </div>
          </div>

          {/* Account details */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {channel === 'bank_transfer' ? 'IBAN / Coordonnées bancaires' : channel === 'mobile_money' ? 'Numéro Mobile Money' : 'ID Compte Paystack'}
            </label>
            <input
              type="text"
              value={accountDetails}
              onChange={e => setAccountDetails(e?.target?.value)}
              placeholder={channel === 'bank_transfer' ? 'FR76 XXXX XXXX XXXX XXXX XXXX XXX' : channel === 'mobile_money' ? '+225 07 XX XX XX XX' : 'paystack_account_id'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Note (optionnel)</label>
            <textarea
              value={note}
              onChange={e => setNote(e?.target?.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
              placeholder="Référence de paiement, instructions spéciales..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Annuler
            </button>
            <button
              onClick={() => onConfirm({ payout, targetCurrency, channel, accountDetails, note, convertedAmount, netAmount, rate, feeAmount })}
              disabled={!accountDetails?.trim()}
              className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              Confirmer le paiement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const MultiCurrencyPayoutManager = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fxRates, setFxRates] = useState(FALLBACK_RATES);
  const [fxLoading, setFxLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [toast, setToast] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [processingId, setProcessingId] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Fetch live FX rates from a free public API
  const fetchFXRates = useCallback(async () => {
    setFxLoading(true);
    try {
      // Using exchangerate-api.com free tier (no key needed for basic)
      const res = await fetch('https://open.er-api.com/v6/latest/XOF');
      if (res?.ok) {
        const data = await res?.json();
        if (data?.rates) {
          setFxRates({ XOF: 1, EUR: data?.rates?.EUR, USD: data?.rates?.USD, GHS: data?.rates?.GHS });
          setLastUpdated(new Date()?.toISOString());
        }
      }
    } catch {
      // Use fallback rates silently
      setLastUpdated(new Date()?.toISOString());
    } finally {
      setFxLoading(false);
    }
  }, []);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      let q = supabase?.from('commission_payouts')?.select(`
        id, escrow_transaction_id, recipient_id, recipient_role, recipient_name, recipient_email,
        commission_rate, base_amount, commission_amount, currency, status, released_at, notes, created_at,
        payout_currency, payout_channel, converted_amount, net_payout_amount, fx_rate_applied
      `)?.order('created_at', { ascending: false })?.limit(200);

      if (filterRole !== 'all') q = q?.eq('recipient_role', filterRole);
      if (filterStatus !== 'all') q = q?.eq('status', filterStatus);

      const { data, error } = await q;
      if (!error) setPayouts(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterRole, filterStatus]);

  useEffect(() => {
    fetchPayouts();
    fetchFXRates();
  }, [fetchPayouts, fetchFXRates]);

  const handleConfirmConversion = async ({ payout, targetCurrency, channel, accountDetails, note, convertedAmount, netAmount, rate, feeAmount }) => {
    setProcessingId(payout?.id);
    try {
      const { error } = await supabase?.from('commission_payouts')?.update({
        payout_currency: targetCurrency,
        payout_channel: channel,
        payout_account_details: accountDetails,
        converted_amount: convertedAmount,
        net_payout_amount: netAmount,
        fx_rate_applied: rate,
        fx_fee_amount: feeAmount,
        status: 'released',
        released_at: new Date()?.toISOString(),
        notes: note || payout?.notes,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', payout?.id);

      if (error) throw error;

      // Log action
      await supabase?.from('escrow_action_logs')?.insert({
        escrow_transaction_id: payout?.escrow_transaction_id,
        action: 'commission_release',
        notes: `Paiement ${targetCurrency} via ${PAYOUT_CHANNELS?.[channel]?.label} — Taux: ${rate?.toFixed(6)} — Net: ${netAmount?.toFixed(2)} ${targetCurrency}`,
        amount_affected: payout?.commission_amount,
        currency: 'XOF',
        metadata: { target_currency: targetCurrency, channel, converted_amount: convertedAmount, net_amount: netAmount, fx_rate: rate },
      });

      showToast(`Paiement de ${formatAmount(netAmount, targetCurrency)} confirmé pour ${payout?.recipient_name}`);
      setSelectedPayout(null);
      fetchPayouts();
    } catch (e) {
      showToast('Erreur lors du traitement du paiement', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const roleConfig = {
    agent: { label: 'Agent', color: 'bg-blue-100 text-blue-700', icon: '🏠' },
    notary: { label: 'Notaire', color: 'bg-purple-100 text-purple-700', icon: '⚖️' },
    platform: { label: 'Plateforme', color: 'bg-amber-100 text-amber-700', icon: '🏢' },
  };

  const statusConfig = {
    pending: { label: 'En attente', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    released: { label: 'Payé', color: 'bg-green-50 text-green-700 border-green-200' },
    cancelled: { label: 'Annulé', color: 'bg-gray-50 text-gray-500 border-gray-200' },
  };

  const totalPending = payouts?.filter(p => p?.status === 'pending')?.reduce((s, p) => s + (p?.commission_amount || 0), 0);
  const totalReleased = payouts?.filter(p => p?.status === 'released')?.reduce((s, p) => s + (p?.commission_amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Paiements Multi-Devises</h2>
          <p className="text-sm text-gray-500 mt-0.5">EUR, USD, GHS avec taux de change en direct</p>
        </div>
        <button onClick={fetchPayouts} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-medium transition-colors">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* FX Rate Banner */}
      <FXRateBanner rates={fxRates} lastUpdated={lastUpdated} onRefresh={fetchFXRates} loading={fxLoading} />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'En attente', value: formatAmount(totalPending), color: 'text-amber-600', bg: 'bg-amber-50', icon: '⏳' },
          { label: 'Payé (XOF)', value: formatAmount(totalReleased), color: 'text-green-600', bg: 'bg-green-50', icon: '✅' },
          { label: 'Équiv. EUR', value: formatAmount(totalPending * (fxRates?.EUR || FALLBACK_RATES?.EUR), 'EUR'), color: 'text-blue-600', bg: 'bg-blue-50', icon: '🇪🇺' },
          { label: 'Équiv. USD', value: formatAmount(totalPending * (fxRates?.USD || FALLBACK_RATES?.USD), 'USD'), color: 'text-purple-600', bg: 'bg-purple-50', icon: '🇺🇸' },
        ]?.map((s, i) => (
          <div key={i} className={`${s?.bg} rounded-xl p-4 border border-white`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{s?.icon}</span>
              <span className="text-xs text-gray-500">{s?.label}</span>
            </div>
            <p className={`text-lg font-bold ${s?.color}`}>{s?.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Rôle:</span>
          {['all', 'agent', 'notary']?.map(r => (
            <button key={r} onClick={() => setFilterRole(r)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterRole === r ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {r === 'all' ? 'Tous' : r === 'agent' ? 'Agents' : 'Notaires'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Statut:</span>
          {['all', 'pending', 'released']?.map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s === 'all' ? 'Tous' : s === 'pending' ? 'En attente' : 'Payés'}
            </button>
          ))}
        </div>
      </div>

      {/* Payouts Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Destinataire', 'Rôle', 'Montant XOF', 'Devise paiement', 'Canal', 'Statut', 'Date', 'Action']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Chargement...</td></tr>
              ) : payouts?.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Aucun paiement trouvé</td></tr>
              ) : payouts?.map(p => {
                const rc = roleConfig?.[p?.recipient_role] || roleConfig?.agent;
                const sc = statusConfig?.[p?.status] || statusConfig?.pending;
                const payoutCurrency = p?.payout_currency;
                const payoutChannel = p?.payout_channel ? PAYOUT_CHANNELS?.[p?.payout_channel] : null;
                return (
                  <tr key={p?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{p?.recipient_name || '—'}</p>
                        <p className="text-xs text-gray-400">{p?.recipient_email || '—'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${rc?.color}`}>
                        {rc?.icon} {rc?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-800">{formatAmount(p?.commission_amount)}</td>
                    <td className="px-4 py-3">
                      {payoutCurrency ? (
                        <div>
                          <p className="font-medium text-gray-800">{CURRENCIES?.find(c => c?.code === payoutCurrency)?.flag} {payoutCurrency}</p>
                          {p?.net_payout_amount && <p className="text-xs text-green-600">{formatAmount(p?.net_payout_amount, payoutCurrency)}</p>}
                          {p?.fx_rate_applied && <p className="text-xs text-gray-400">Taux: {p?.fx_rate_applied?.toFixed(6)}</p>}
                        </div>
                      ) : <span className="text-gray-400 text-xs">Non défini</span>}
                    </td>
                    <td className="px-4 py-3">
                      {payoutChannel ? (
                        <span className="text-xs text-gray-700">{payoutChannel?.icon} {payoutChannel?.label}</span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${sc?.color}`}>
                        {sc?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(p?.created_at)}</td>
                    <td className="px-4 py-3">
                      {p?.status === 'pending' && (
                        <button
                          onClick={() => setSelectedPayout(p)}
                          disabled={processingId === p?.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                        >
                          <ArrowRightLeft className="w-3 h-3" />
                          Payer
                        </button>
                      )}
                      {p?.status === 'released' && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle className="w-3.5 h-3.5" /> Payé
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conversion Modal */}
      {selectedPayout && (
        <ConversionModal
          payout={selectedPayout}
          rates={fxRates}
          onClose={() => setSelectedPayout(null)}
          onConfirm={handleConfirmConversion}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${toast?.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast?.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast?.msg}
        </div>
      )}
    </div>
  );
};

export default MultiCurrencyPayoutManager;
